/**
 * @fileoverview Componente responsável por gerenciar cargos da empresa ativa.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Paper,
  Chip,
  Typography,
  IconButton,
  Button,
  makeStyles
} from "@material-ui/core";
import { Pencil, Trash2 as Trash, Plus } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import JobRoleModal from "../JobRoleModal";

const GRID_COLUMNS = "72px 1fr 104px";
const GRID_COLUMNS_XS = "56px 1fr 88px";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.mode === "light" ? "#1e293b" : "#FFFFFF",
  },
  buttonsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  addButton: {
    height: 36,
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    padding: "0 14px",
  },
  mainPaper: {
    overflowY: "auto",
    backgroundColor: "transparent",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "12px 20px",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(0, 0, 0, 0.06)",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: GRID_COLUMNS_XS,
      padding: "10px 12px",
    },
  },
  listHeaderText: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : "#94a3b8",
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: theme.spacing(1),
  },
  listRow: {
    display: "grid",
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: "center",
    padding: "14px 20px",
    borderRadius: "12px",
    transition: "background 0.15s ease",
    cursor: "default",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.06)"
          : "rgba(61, 92, 253, 0.04)",
    },
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.04)"
        : "1px solid rgba(0, 0, 0, 0.04)",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: GRID_COLUMNS_XS,
      padding: "10px 12px",
    },
  },
  roleId: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "#94a3b8",
    fontVariantNumeric: "tabular-nums",
  },
  roleNameCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  roleChip: {
    fontWeight: 600,
    fontSize: "0.85rem",
    borderRadius: "8px",
    height: 28,
    maxWidth: "100%",
    color: theme.palette.type === "dark" ? "#dbeafe" : "#1e3a8a",
    background:
      theme.palette.type === "dark"
        ? "rgba(59, 130, 246, 0.18)"
        : "rgba(59, 130, 246, 0.12)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(96, 165, 250, 0.22)"
        : "1px solid rgba(59, 130, 246, 0.14)",
    "& .MuiChip-label": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      paddingLeft: 12,
      paddingRight: 12,
    },
  },
  actionsCell: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: 6,
    borderRadius: "8px",
    transition: "all 0.15s ease",
  },
  editButton: {
    color: theme.palette.type === "dark" ? "#93c5fd" : "#3b82f6",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(59, 130, 246, 0.08)",
    },
  },
  deleteButton: {
    color: theme.palette.type === "dark" ? "#fca5a5" : "#ef4444",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(239, 68, 68, 0.15)"
          : "rgba(239, 68, 68, 0.08)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.35)"
        : "#94a3b8",
    fontSize: "0.95rem",
  },
}));

const JobRolesManager = () => {
  const classes = useStyles();
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJobRole, setSelectedJobRole] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadJobRoles = useCallback(async () => {
    try {
      const { data } = await api.get("/job-roles");
      setJobRoles(data || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    loadJobRoles();
  }, [loadJobRoles]);

  const handleOpenCreate = () => {
    setSelectedJobRole(null);
    setModalOpen(true);
  };

  const handleOpenEdit = jobRole => {
    setSelectedJobRole(jobRole);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedJobRole(null);
    setModalOpen(false);
  };

  const handleSubmit = async ({ name }) => {
    setLoading(true);
    try {
      if (selectedJobRole?.id) {
        await api.put(`/job-roles/${selectedJobRole.id}`, { name });
        toast.success(i18n.t("settings.jobRoles.toasts.updated"));
      } else {
        await api.post("/job-roles", { name });
        toast.success(i18n.t("settings.jobRoles.toasts.created"));
      }

      handleCloseModal();
      await loadJobRoles();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedJobRole?.id) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/job-roles/${selectedJobRole.id}`);
      toast.success(i18n.t("settings.jobRoles.toasts.deleted"));
      setConfirmOpen(false);
      setSelectedJobRole(null);
      await loadJobRoles();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("settings.jobRoles.confirmationModal.deleteTitle")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("settings.jobRoles.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <JobRoleModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        initialData={selectedJobRole}
        loading={loading}
      />

      <div className={classes.header}>
        <div />
        <div className={classes.buttonsWrapper}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus />}
            onClick={handleOpenCreate}
            className={classes.addButton}
          >
            {i18n.t("settings.jobRoles.buttons.add")}
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper}>
        <div className={classes.listHeader}>
          <Typography className={classes.listHeaderText}>ID</Typography>
          <Typography className={classes.listHeaderText}>
            {i18n.t("settings.jobRoles.title")}
          </Typography>
          <Typography
            className={classes.listHeaderText}
            style={{ textAlign: "right" }}
          >
            Ações
          </Typography>
        </div>

        <div className={classes.listContainer}>
          {jobRoles.length === 0 ? (
            <Typography className={classes.emptyState}>
              {i18n.t("settings.jobRoles.emptyState")}
            </Typography>
          ) : (
            jobRoles.map(jobRole => (
              <div key={jobRole.id} className={classes.listRow}>
                <Typography className={classes.roleId}>
                  #{jobRole.id}
                </Typography>

                <div className={classes.roleNameCell}>
                  <Chip
                    className={classes.roleChip}
                    label={jobRole.name}
                    size="small"
                  />
                </div>

                <div className={classes.actionsCell}>
                  <IconButton
                    size="small"
                    className={`${classes.iconButton} ${classes.editButton}`}
                    onClick={() => handleOpenEdit(jobRole)}
                    title="Editar"
                  >
                    <Pencil size={16} strokeWidth={1.5} />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={`${classes.iconButton} ${classes.deleteButton}`}
                    onClick={() => {
                      setSelectedJobRole(jobRole);
                      setConfirmOpen(true);
                    }}
                    title="Excluir"
                  >
                    <Trash size={16} strokeWidth={1.5} />
                  </IconButton>
                </div>
              </div>
            ))
          )}
        </div>
      </Paper>
    </div>
  );
};

export default JobRolesManager;
